import debounce from 'lodash/debounce';
import isomorphicFetch from 'isomorphic-fetch';
import { format } from 'url';

export default function createResource({
    endpoint,
    idAttribute = 'id',
    idLocation = 'query',
    waitTime = 50,
    maxWaitTime = 250,
}) {
    if (typeof endpoint === 'undefined') {
        throw new Error('Expected `endpoint` to be specified.');
    } else if (typeof endpoint !== 'string') {
        throw new Error('Expected `endpoint` to be a string.');
    }

    if (typeof idAttribute !== 'string') {
        throw new Error('Expected `idAttribute` to be a string.');
    }

    if (idLocation !== 'query' && idLocation !== 'path') {
        throw new Error(`Expected \`idLocation\` to be 'path' or 'query', but was '${idLocation}.'`);
    }

    if (typeof waitTime !== 'number') {
        throw new Error('Expected `waitTime` to be a number.');
    }

    if (typeof maxWaitTime !== 'number') {
        throw new Error('Expected `maxWaitTime` to be a number.');
    }

    const batchRequests = {};
    let isWaiting = false;

    /**
     * Forces all pending batch requests to delay fetching until `unwait` is invoked.
     *
     * This is useful when you are rendering multiple components on a page
     * (e.g. multiple timesgrids on a third party site) and want to explicitly
     * control when the batch requests get made rather than relying on the debounce
     * to batch all the calls.
     */
    function wait() {
        isWaiting = true;
    }

    /**
     * Forces all pending batch requests to immediately fetch.
     */
    function unwait() {
        isWaiting = false;
        Object.values(batchRequests).forEach(request => request.fetchImmediately());
    }

    /**
     * Creates a batch request object for the specified query and options.
     */
    function createBatchRequest(query, options = {}) {
        if (options.method && options.method !== 'GET') {
            throw new Error(`Only GET requests are allowed on batch resources, but ${options.method} was specified.`);
        }

        const ids = new Set();
        const promises = [];
        let isFetching = false;

        /**
         * Creates a new promise that will be resolved or rejected whenever
         * the fetch is invoked.
         */
        function createPromise() {
            return new Promise((resolve, reject) => {
                promises.push({
                    resolve,
                    reject,
                });
            });
        }

        /**
         * Resolves all the promises with clones of the HTTP response.
         */
        const resolve = (httpResponse, ...args) => {
            // To prevent a race condition, we must create all the clones before passing them back to the promises.
            const responses = promises.map(() => httpResponse.clone());

            promises.forEach((promise, index) => {
                try {
                    promise.resolve(responses[index], ...args);
                } catch (e) {
                    console.error(e); // eslint-disable-line no-console
                }
            });
        };

        /**
         * Rejects all the promises with the error message.
         */
        const reject = (...args) => {
            promises.forEach(promise => {
                try {
                    promise.reject(...args);
                } catch (e) {
                    console.error(e); // eslint-disable-line no-console
                }
            });
        };

        /**
         * Gets the URL endpoint to hit.
         */
        function getUrl() {
            if (idLocation === 'path') {
                return format({
                    pathname: `${endpoint}/${Array.from(ids).join(';')}`,
                    query,
                });
            } else {
                return format({
                    pathname: endpoint,
                    query: {
                        [idAttribute]: Array.from(ids).join(','),
                        ...query,
                    },
                });
            }
        }

        /**
         * Immediately performs a fetch for all the IDs batched within this batch request.
         */
        function fetchImmediately() {
            isFetching = true;

            if (ids.size === 0) {
                return;
            }

            const url = getUrl();

            isomorphicFetch(url, {
                ...options,
                method: 'GET',
            })
                .then(resolve)
                .catch(reject);
        }

        /**
         * Performs a fetch for all the IDs batched within this batch request after `waitTime` has passed
         * since the last IDs were requested.
         *
         * The fetch will not be delayed more than `maxWaitTime` from the first time it is requested.
         *
         * If `wait` has been invoked on the resource, the fetch will not occur until `unwait` is called,
         * regardless of the `waitTime` and `maxWaitTime` settings.
         */
        const debouncedFetch = debounce(() => {
            if (!isWaiting) {
                fetchImmediately();
            }
        }, waitTime, {
            maxWait: maxWaitTime,
        });

        /**
         * Adds the specified IDs to this batch request and triggers the debounced fetch.
         *
         * Returns a promise that will be resolved with the HTTP response whenever the fetch is invoked.
         */
        function tryFetch(idsToFetch) {
            idsToFetch.forEach(id => ids.add(id));

            const promise = createPromise();

            if (!isWaiting) {
                debouncedFetch();
            }

            return promise;
        }

        return {
            tryFetch,
            fetchImmediately,
            isFetching: () => isFetching,
        };
    }

    /**
     * Gets a batch request object that matches the given query and options.
     *
     * If there is an existing matching batch request, it will be returned so
     * the fetches can be batched.
     *
     * If there is no matching batch request, a new one will be created.
     */
    function getBatchRequest(query, options) {
        const key = JSON.stringify({
            query,
            options,
        });

        if (!batchRequests[key] || batchRequests[key].isFetching()) {
            batchRequests[key] = createBatchRequest(query, options);
        }

        return batchRequests[key];
    }

    /**
     * Splits the parameters up into an array of IDs and the rest of the query parameters.
     */
    function parseParameters(parameters) {
        if (typeof parameters === 'number' || typeof parameters === 'string') {
            return {
                ids: [parameters],
                query: {},
            };
        }

        if (Array.isArray(parameters)) {
            return {
                ids: parameters,
                query: {},
            };
        }

        let {
            [idAttribute]: ids,
            ...query // eslint-disable-line prefer-const
        } = parameters;

        if (typeof ids === 'undefined') {
            throw new Error(`At least one ${idAttribute} must be specified.`);
        }

        if (!Array.isArray(ids)) {
            ids = [ids];
        }

        return {
            ids,
            query,
        };
    }

    /**
     * Gets a promise that will be resolved whenever the fetch for the given parameters
     * and options is fulfilled.
     *
     * Based on the specified parameters and options, the fetch may be batched with
     * matching fetches if they are requested within the specified `waitTime`.
     */
    function fetch(parameters, options) {
        const {
            ids,
            query,
        } = parseParameters(parameters);

        const request = getBatchRequest(query, options);

        return request.tryFetch(ids);
    }

    return {
        fetch,
        wait,
        unwait,
    };
}
